import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api';
import { PageHeader, Loading, ErrorBox, Badge } from '../components/ui';

// Booking status values from apps/api (services/dispatch.ts): assigned, en_route_pickup,
// picked_up, in_transit, delivered, cancelled.
const BOOKING_TONE: Record<string, 'success' | 'warn' | 'danger' | 'neutral'> = {
  delivered: 'success',
  cancelled: 'danger',
};
const bookingTone = (status: string) => BOOKING_TONE[status] ?? 'neutral';

export function PartnersPage() {
  const qc = useQueryClient();
  const partners = useQuery({ queryKey: ['admin-partners'], queryFn: () => adminApi.partners() });
  const bookings = useQuery({ queryKey: ['admin-bookings'], queryFn: () => adminApi.bookings() });
  const healthcheck = useMutation({
    mutationFn: (id: string) => adminApi.partnerHealthcheck(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-partners'] }),
  });

  if (partners.isLoading) return <Loading />;
  if (partners.error) return <ErrorBox error={partners.error} />;
  const items = (partners.data as any)?.items ?? [];

  return (
    <div>
      <PageHeader title="Riders & Partners" subtitle="Courier partner APIs behind Nova Logistics" />
      <div className="grid grid-cols-3 gap-4 mb-6">
        {items.map((p: any) => (
          <div key={p.id} className="card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: p.status === 'connected' ? 'var(--success)' : p.status === 'error' ? 'var(--danger)' : 'var(--text-faint)' }} />
                <span className="font-semibold">{p.name}</span>
              </div>
              <Badge tone={p.status === 'connected' ? 'success' : p.status === 'error' ? 'danger' : 'neutral'}>{p.status}</Badge>
            </div>
            <div className="text-xs mb-1" style={{ color: 'var(--text-faint)' }}>via {p.via}</div>
            <div className="grid grid-cols-2 gap-2 text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
              <div>Uptime: <b>{p.uptimePct}%</b></div>
              <div>Avg response: <b>{p.avgResponseMs}ms</b></div>
              <div>Today: <b>{p.deliveriesToday}</b></div>
              <div>Avg cost: <b>{p.avgCost}</b></div>
            </div>
            <button className="btn-outline" onClick={() => healthcheck.mutate(p.id)} disabled={healthcheck.isPending}>Run healthcheck</button>
          </div>
        ))}
        {!items.length && <div className="col-span-3 card p-10 text-center text-sm" style={{ color: 'var(--text-faint)' }}>No partner data yet</div>}
      </div>

      <div className="card p-4">
        <div className="font-semibold mb-3">Recent bookings</div>
        <table>
          <thead><tr><th>Order</th><th>Partner</th><th>Rider</th><th>Status</th><th>Booked</th></tr></thead>
          <tbody>
            {((bookings.data as any)?.items ?? []).map((b: any) => (
              <tr key={b.id}><td>{b.orderId}</td><td>{b.partner}</td><td>{b.rider}</td><td><Badge tone={bookingTone(b.status)}>{b.status}</Badge></td><td>{b.bookedAt}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
